import { useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import { ComponentType, useMemo } from 'react';
import { useChangePasswordStore } from '@shared/store/useChangePasswordStore';
import {
  createChangePasswordFormSubmitHandler,
  getChangePasswordFormCopy,
} from '@modules/change-password/business_logic';
import { useChangePasswordSubmit } from '../hooks/useChangePasswordSubmit';
import type { ChangePasswordProps } from '../views/change_password_view';

interface ChangePasswordContainerProps {
  View: ComponentType<ChangePasswordProps>;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
  onSuccessNavigate: () => void;
  onNavigateBack: () => void;
  useFormSubmitHandler?: boolean;
}

export function ChangePasswordContainer({
  View,
  onSuccess,
  onError,
  onSuccessNavigate,
  onNavigateBack,
  useFormSubmitHandler = false,
}: ChangePasswordContainerProps) {
  const currentUser = useQuery(api.password.queries.getCurrentUser, {});
  const isSubmitting = useChangePasswordStore((s) => s.isSubmitting);
  const form = useChangePasswordStore((s) => s.form);
  const setForm = useChangePasswordStore((s) => s.setForm);

  const handleSubmit = useChangePasswordSubmit(onSuccess, onError, onSuccessNavigate);

  const formCopy = useMemo(() => getChangePasswordFormCopy(isSubmitting), [isSubmitting]);

  const onFormSubmit = useMemo(
    () => (useFormSubmitHandler ? createChangePasswordFormSubmitHandler(handleSubmit) : undefined),
    [useFormSubmitHandler, handleSubmit]
  );

  const isLoading = currentUser === undefined;
  const canChangePassword = Boolean(currentUser?.email && !currentUser.isAnonymous);

  return (
    <View
      {...formCopy}
      isLoading={isLoading}
      canChangePassword={canChangePassword}
      isSubmitting={isSubmitting}
      currentPassword={form.currentPassword}
      newPassword={form.newPassword}
      confirmPassword={form.confirmPassword}
      onCurrentPasswordChange={(currentPassword) => setForm({ currentPassword })}
      onNewPasswordChange={(newPassword) => setForm({ newPassword })}
      onConfirmPasswordChange={(confirmPassword) => setForm({ confirmPassword })}
      onSubmit={handleSubmit}
      onNavigateBack={onNavigateBack}
      onFormSubmit={onFormSubmit}
    />
  );
}

export type { ChangePasswordProps };
